import { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

interface HelpPanelProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

/**
 * Aufklappbares Hilfe-Panel für die Integrationsseiten.
 * Zeigt kontextbezogene Anleitungen für Administratoren und externe Systeme.
 */
export default function HelpPanel({ title, children, defaultOpen = false }: HelpPanelProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary/10 transition-colors w-full text-left"
      >
        <HelpCircle size={16} className="shrink-0" />
        {title}
        {open ? (
          <ChevronUp size={14} className="ml-auto shrink-0" />
        ) : (
          <ChevronDown size={14} className="ml-auto shrink-0" />
        )}
      </button>

      {open && (
        <div className="mt-2 rounded-md border bg-card px-5 py-4 text-sm leading-relaxed text-foreground space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Hilfs-Komponenten für einheitliche Darstellung ─────────────────────── */

interface HelpSectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

export function HelpSection({ icon, title, children }: HelpSectionProps) {
  return (
    <div className="pb-4 border-b last:border-b-0 last:pb-0">
      <h4 className="flex items-center gap-2 font-medium text-foreground mb-1.5">
        <span className="text-primary shrink-0">{icon}</span>
        {title}
      </h4>
      <div className="text-muted-foreground text-[13px] leading-relaxed">
        {children}
      </div>
    </div>
  );
}

export function HelpCode({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
      {children}
    </code>
  );
}

interface HelpStepProps {
  number: number;
  children: React.ReactNode;
}

export function HelpStep({ number, children }: HelpStepProps) {
  return (
    <div className="flex items-center gap-2.5 my-1.5">
      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-medium shrink-0">
        {number}
      </span>
      <span className="text-[13px] text-muted-foreground">{children}</span>
    </div>
  );
}
